import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './focusRing';

/** Border / underline shell shared by Input2 and InputGroup2 (no padding or focus). */
export const input2ToneVariantShell = cva('', {
  variants: {
    tone: {
      regular: 'rounded text-fg-default',
      inline: [
        'transition-all',
        'bg-transparent text-fg-default',
        'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
        'shadow-[inset_0_-1px_0_0_]',
        'shadow-border-default',
        'not-aria-readonly:hover:shadow-bg-inset',
        'not-aria-readonly:focus:shadow-brand-300',
        'not-aria-readonly:hover:focus:shadow-brand-400'
      ],
      plain: [
        'transition-all',
        'bg-transparent text-fg-default',
        'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
        'shadow-[inset_0_-1px_0_0_]',
        'shadow-transparent',
        'not-aria-readonly:hover:shadow-bg-inset',
        'not-aria-readonly:focus:shadow-brand-300',
        'not-aria-readonly:hover:focus:shadow-brand-400'
      ]
    },
    variant: {
      default: '',
      danger: '',
      /** Something to look at that doesn't block (a hint), as opposed to `danger`. */
      attention: ''
    }
  },
  compoundVariants: [
    {
      tone: 'regular',
      variant: 'default',
      className: [
        'border border-border-default bg-bg-default',
        'not-aria-readonly:hocus:border-bg-inset'
      ]
    },
    {
      tone: 'regular',
      variant: 'danger',
      className: [
        'border border-border-danger bg-bg-danger',
        'not-aria-readonly:hocus:border-border-danger-emphasis',
        'not-aria-readonly:hocus:bg-bg-danger/80'
      ]
    },
    {
      tone: ['inline', 'plain'],
      variant: 'danger',
      className: [
        'shadow-border-danger',
        'not-aria-readonly:hover:shadow-border-danger-emphasis',
        'not-aria-readonly:focus:shadow-border-danger-emphasis',
        'not-aria-readonly:hover:focus:shadow-border-danger-emphasis'
      ]
    },
    {
      tone: 'regular',
      variant: 'attention',
      className: [
        'border border-border-attention-emphasis bg-bg-default',
        'not-aria-readonly:hocus:border-border-attention-emphasis'
      ]
    },
    {
      tone: ['inline', 'plain'],
      variant: 'attention',
      className: [
        'shadow-border-attention-emphasis',
        'not-aria-readonly:hover:shadow-border-attention-emphasis',
        'not-aria-readonly:focus:shadow-border-attention-emphasis',
        'not-aria-readonly:hover:focus:shadow-border-attention-emphasis'
      ]
    }
  ],
  defaultVariants: {
    tone: 'regular',
    variant: 'default'
  }
});

/** Padding and focus chrome for standalone Input2 / TextArea2. */
export const input2FieldChrome = cva('', {
  variants: {
    tone: {
      regular: ['px-3', focusRings.focus.normal, focusRings.focus.visible],
      inline: '',
      plain: ''
    }
  },
  defaultVariants: {
    tone: 'regular'
  }
});

/** Wrapper-only chrome for InputGroup2 (input padding, focus-within). */
export const inputGroup2FieldChrome = cva('', {
  variants: {
    tone: {
      regular: '',
      inline: '',
      plain: ''
    },
    variant: {
      default: '',
      danger: '',
      /** Something to look at that doesn't block (a hint), as opposed to `danger`. */
      attention: ''
    }
  },
  compoundVariants: [
    {
      tone: 'regular',
      className: [
        '[&_input]:first:pl-2 [&_input]:last:pr-2',
        '[&_[data-element="input-group-input"]]:first:pl-2 [&_[data-element="input-group-input"]]:last:pr-2',
        focusRings.focusWithin.normal,
        focusRings.focusWithin.visible
      ]
    },
    {
      tone: ['inline', 'plain'],
      variant: 'default',
      className: [
        'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_] aria-expanded:shadow-[inset_0_-2px_0_0_]',
        'not-aria-readonly:has-focus:shadow-brand-300',
        'not-aria-readonly:hover:focus-within:shadow-brand-400 aria-expanded:shadow-brand-400'
      ]
    },
    {
      tone: ['inline', 'plain'],
      variant: 'danger',
      className: [
        'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_] aria-expanded:shadow-[inset_0_-2px_0_0_]',
        'not-aria-readonly:has-focus:shadow-border-danger-emphasis',
        'not-aria-readonly:hover:focus-within:shadow-border-danger-emphasis aria-expanded:shadow-border-danger-emphasis'
      ]
    },
    {
      tone: ['inline', 'plain'],
      variant: 'attention',
      className: [
        'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_] aria-expanded:shadow-[inset_0_-2px_0_0_]',
        'not-aria-readonly:has-focus:shadow-border-attention-emphasis',
        'not-aria-readonly:hover:focus-within:shadow-border-attention-emphasis aria-expanded:shadow-border-attention-emphasis'
      ]
    }
  ],
  defaultVariants: {
    tone: 'regular',
    variant: 'default'
  }
});

export const input2Size = cva('', {
  variants: {
    size: {
      xs: 'text-base h-[20px] py-1',
      sm: 'text-base h-9 py-1.5',
      md: 'text-base h-12 py-2',
      lg: 'text-lg h-15 py-3 font-semibold',
      xl: 'text-xl h-17 py-4 font-semibold',
      /** A page title edited where it stands, in the page title's type. */
      display:
        'text-3xl md:text-[44px] h-auto py-0 font-semibold leading-tight md:leading-[1.1]'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

export const textArea2Size = cva('', {
  variants: {
    size: {
      xs: 'text-base h-[20px] min-h-[20px] py-1 leading-[8px]',
      sm: 'text-base h-9 min-h-9 py-1.5 leading-[17px]',
      md: 'text-base h-12 min-h-12 py-2 leading-[23px]',
      lg: 'text-lg h-15 min-h-15 py-3 font-semibold leading-[25px]',
      xl: 'text-xl h-17 min-h-17 py-4 font-semibold leading-[23px]',
      /** A page title edited where it stands, in the page title's type. */
      display:
        'text-3xl md:text-[44px] h-auto min-h-0 py-0 font-semibold leading-tight md:leading-[1.1]'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

export const inputGroup2Size = cva('', {
  variants: {
    size: {
      xs: 'text-base min-h-[20px] [&_svg]:size-3 [&_[data-element=input-group-button]]:px-1.5 [&_[data-element=input-group-button]]:min-h-[20px]',
      sm: 'text-base min-h-9 [&_svg]:size-4 [&_[data-element=input-group-button]]:px-2.5 [&_[data-element=input-group-button]]:min-h-9',
      md: 'text-base min-h-12 [&_svg]:size-5 [&_[data-element=input-group-button]]:px-3.5 [&_[data-element=input-group-button]]:min-h-12',
      lg: 'text-lg min-h-15 [&_svg]:size-6 [&_[data-element=input-group-button]]:px-5 [&_[data-element=input-group-button]]:min-h-15',
      xl: 'text-xl min-h-17 [&_svg]:size-6 [&_[data-element=input-group-button]]:px-5 [&_[data-element=input-group-button]]:min-h-17',
      display:
        'text-3xl md:text-[44px] min-h-10 [&_svg]:size-7 [&_[data-element=input-group-button]]:px-5 [&_[data-element=input-group-button]]:min-h-10'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

export const input2Base = [
  'outline-none overflow-auto',
  'flex h-10 w-full py-2',
  'file:border-0 file:bg-transparent file:text-base file:font-medium file:text-fg-default',
  'placeholder:text-fg-muted',
  'disabled:cursor-not-allowed aria-readonly:cursor-default disabled:opacity-50'
];

export const textArea2Base = [
  'outline-none overflow-hidden max-h-80',
  'flex h-10 w-full py-2',
  'file:border-0 file:bg-transparent file:text-base file:font-medium file:text-fg-default',
  'placeholder:text-fg-muted',
  'disabled:cursor-not-allowed aria-readonly:cursor-default disabled:opacity-50'
];

export const inputGroup2Base = [
  'overflow-hidden',
  'group',
  'cursor-text aria-readonly:cursor-default',
  'flex w-full',
  'file:border-0 file:bg-transparent file:text-base file:font-medium file:text-fg-default',
  'placeholder:text-fg-muted',
  'disabled:cursor-not-allowed disabled:opacity-50',
  '[&_[data-element=input-group-slot]]:px-3'
];

export type Input2Tone = NonNullable<
  VariantProps<typeof input2ToneVariantShell>['tone']
>;
export type Input2Variant = NonNullable<
  VariantProps<typeof input2ToneVariantShell>['variant']
>;
export type Input2Size = NonNullable<VariantProps<typeof input2Size>['size']>;

export type Input2ChromeProps = VariantProps<typeof input2ToneVariantShell> &
  VariantProps<typeof input2Size> & {
    tone?: Input2Tone;
    variant?: Input2Variant;
    size?: Input2Size;
  };

export type InputGroup2ChromeProps = VariantProps<
  typeof input2ToneVariantShell
> &
  VariantProps<typeof inputGroup2FieldChrome> &
  VariantProps<typeof inputGroup2Size> & {
    tone?: Input2Tone;
    variant?: Input2Variant;
    size?: Input2Size;
  };

export function input2ClassName({
  tone,
  variant,
  size
}: Input2ChromeProps): string {
  return cn(
    input2Base,
    input2ToneVariantShell({ tone, variant }),
    input2FieldChrome({ tone }),
    input2Size({ size }),
    tone === 'regular' && size === 'sm' && 'rounded'
  );
}

export function textArea2ClassName({
  tone,
  variant,
  size
}: Input2ChromeProps): string {
  return cn(
    textArea2Base,
    input2ToneVariantShell({ tone, variant }),
    input2FieldChrome({ tone }),
    textArea2Size({ size }),
    tone === 'regular' && size === 'sm' && 'rounded',
    // No frame to drag from: the height follows the text.
    (tone === 'inline' || tone === 'plain') && 'resize-none'
  );
}

export function inputGroup2ClassName({
  tone,
  variant,
  size
}: InputGroup2ChromeProps): string {
  return cn(
    inputGroup2Base,
    input2ToneVariantShell({ tone, variant }),
    inputGroup2FieldChrome({ tone, variant }),
    inputGroup2Size({ size })
  );
}
